import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from "react";
import * as XLSX from "xlsx";
import {
  listMaster,
  upsertMaster,
  removeMaster,
  summaryMaster,
  similarMaster,
} from "./masterHsApi";
import { masterRowsToAoa } from "./masterHsXlsx";
import { summaryView, totalCount } from "./masterHsSummary";

let _gid = 0;

// 공통 툴바 버튼 — BulkClassifyPanel.jsx의 Btn과 동일 정의(같은 파일 내 관례 준수)
function Btn({ primary, onClick, disabled, children, style }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: "6px 14px",
        fontSize: 13,
        borderRadius: 6,
        cursor: disabled ? "default" : "pointer",
        border: primary ? "none" : "1px solid #d1d5db",
        background: disabled ? "#e5e7eb" : primary ? "#2563eb" : "#fff",
        color: disabled ? "#9ca3af" : primary ? "#fff" : "#374151",
        ...style,
      }}
    >
      {children}
    </button>
  );
}

const COLS = [
  { dataField: "productName", headerText: "상품명", width: 220 },
  { dataField: "composition", headerText: "성분", width: 140 },
  { dataField: "supplier", headerText: "공급자", width: 110 },
  {
    dataField: "rate",
    headerText: "일치율",
    width: 64,
    labelFunction: (rowIndex, columnIndex, value, headerText, item) =>
      item?.rate ? `${item.rate}%` : "",
  },
  {
    dataField: "hsk",
    headerText: "세번",
    width: 120,
    style: "audit-grid-mono",
  },
  {
    dataField: "confirmedAt",
    headerText: "확정일",
    width: 110,
    // 5-인자: rowIndex, columnIndex, value, headerText, item — repo 공통 시그니처
    labelFunction: (rowIndex, columnIndex, value, headerText, item) =>
      item?.confirmedAt ? String(item.confirmedAt).slice(0, 10) : "",
  },
  { dataField: "userId", headerText: "저장자", width: 110 },
];

/** 마스터 세번관리 패널 — 검색·조회 + 체크행 일괄 세번수정 + 삭제 + 엑셀 다운로드. */
export default function MasterHsPanel({ embedded }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [hskInput, setHskInput] = useState("");
  const [banner, setBanner] = useState("");
  const [loading, setLoading] = useState(false);
  const [deleteArmed, setDeleteArmed] = useState(false);
  const [summary, setSummary] = useState([]); // [{hsk,cnt,label}]
  const [summaryTotal, setSummaryTotal] = useState(0);
  const [summaryError, setSummaryError] = useState(false);
  const [hskFilter, setHskFilter] = useState(""); // 좌 패널 선택 세번("" = 전체)
  const [searchMode, setSearchMode] = useState("normal"); // normal|similar
  const gridId = useMemo(() => `master-hs-grid-${++_gid}`, []);
  const gridReady = useRef(false);
  const deleteSnapshotRef = useRef([]);

  const reload = useCallback(async (query, hsk) => {
    setLoading(true);
    try {
      const res = await listMaster({ q: query, hsk });
      setItems(res?.items || []);
    } finally {
      setLoading(false);
    }
  }, []);

  const reloadSummary = useCallback(async () => {
    try {
      const res = await summaryMaster();
      setSummaryError(!!res.error);
      const view = summaryView(res.items);
      setSummary(view);
      setSummaryTotal(res.total || totalCount(res.items));
      if (hskFilter && !view.some((s) => s.hsk === hskFilter)) {
        setHskFilter(""); // 선택 세번이 소멸 — 필터 해제(보이지 않는 필터 방지)
        reload(q, "");
      }
    } catch (e) {
      setSummaryError(true); // 네트워크/비JSON 예외도 오류 상태로(unhandled rejection 방지)
    }
  }, [hskFilter, q, reload]);

  useEffect(() => {
    reload("", "");
    reloadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // AUIGrid 생성/파기 — BulkClassifyPanel과 동일 패턴
  useEffect(() => {
    if (!window.AUIGrid) return;
    const sel = `#${gridId}`;
    if (window.AUIGrid.isCreated(sel)) window.AUIGrid.destroy(sel);
    window.AUIGrid.create(sel, COLS, {
      headerHeight: 32,
      rowHeight: 28,
      showRowNumColumn: true,
      enableSorting: true,
      enableFilter: true,
      selectionMode: "singleRow",
      showRowCheckColumn: true,
    });
    gridReady.current = true;
    return () => {
      if (window.AUIGrid?.isCreated(sel)) window.AUIGrid.destroy(sel);
      gridReady.current = false;
    };
  }, [gridId]);

  // items → grid
  useEffect(() => {
    if (!gridReady.current || !window.AUIGrid) return;
    window.AUIGrid.setGridData(`#${gridId}`, items);
  }, [items, gridId]);

  const onSearch = useCallback(async () => {
    setBanner("");
    setDeleteArmed(false);
    if (searchMode === "similar") {
      if (!q.trim()) {
        setBanner("유사 검색은 검색어가 필요합니다");
        return;
      }
      setLoading(true);
      try {
        const res = await similarMaster(q.trim(), 50);
        setItems(res.error ? [] : res.items || []);
        if (res.error) setBanner("유사 검색에 실패했습니다");
      } finally {
        setLoading(false);
      }
      return;
    }
    reload(q, hskFilter);
  }, [q, hskFilter, reload, searchMode]);

  const onPickHsk = useCallback(
    (hsk) => {
      setHskFilter(hsk);
      setBanner("");
      setDeleteArmed(false);
      reload(q, hsk);
    },
    [q, reload],
  );

  // 체크된 그리드 행들에 세번을 일괄 저장(upsert). 체크 없음/세번 무효는 인라인 배너.
  const applyBulkHsk = useCallback(async () => {
    const hsk = hskInput.replace(/\s/g, "");
    const checked = window.AUIGrid?.getCheckedRowItems(`#${gridId}`) || [];
    if (!checked.length || !/^\d{10}$/.test(hsk)) {
      setBanner("체크한 행과 10자리 세번을 확인하세요");
      return;
    }
    setBanner("");
    setDeleteArmed(false);
    for (const c of checked) {
      const item = c?.item;
      if (!item) continue;
      await upsertMaster({
        keyword: item.keyword,
        hsk,
        productName: item.productName,
        composition: item.composition,
        supplier: item.supplier,
      });
    }
    if (searchMode === "similar" && q.trim()) {
      const res = await similarMaster(q.trim(), 50);
      setItems(res.error ? [] : res.items || []);
    } else {
      await reload(q, hskFilter);
    }
    reloadSummary();
  }, [hskInput, gridId, q, hskFilter, reload, reloadSummary, searchMode]);

  // 삭제 — window.confirm 금지, 2단계 인라인 배너 확인.
  // ARM 시점의 체크 선택을 스냅샷하여 CONFIRM 시점에 그대로 사용(그 사이 체크가
  // 바뀌어도 배너에 고지한 건수·대상과 실제 삭제 대상이 항상 일치하도록 함).
  const onDeleteClick = useCallback(async () => {
    if (!deleteArmed) {
      const checked = window.AUIGrid?.getCheckedRowItems(`#${gridId}`) || [];
      if (!checked.length) {
        setBanner("체크한 행이 없습니다");
        setDeleteArmed(false);
        return;
      }
      const keywords = checked.map((c) => c?.item?.keyword).filter(Boolean);
      deleteSnapshotRef.current = keywords;
      setBanner(
        `체크한 ${keywords.length}건을 삭제합니다 — 한번 더 [삭제] 누르면 확정`,
      );
      setDeleteArmed(true);
      return;
    }
    setDeleteArmed(false);
    setBanner("");
    const keywords = deleteSnapshotRef.current;
    deleteSnapshotRef.current = [];
    for (const keyword of keywords) {
      await removeMaster(keyword);
    }
    if (searchMode === "similar" && q.trim()) {
      const res = await similarMaster(q.trim(), 50);
      setItems(res.error ? [] : res.items || []);
    } else {
      await reload(q, hskFilter);
    }
    reloadSummary();
  }, [gridId, deleteArmed, q, hskFilter, reload, reloadSummary, searchMode]);

  const onDownload = useCallback(() => {
    const aoa = masterRowsToAoa(items);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.aoa_to_sheet(aoa),
      "마스터세번",
    );
    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `마스터세번_${today}.xlsx`);
  }, [items]);

  return (
    <div
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: "#fff",
        position: "relative",
      }}
    >
      <div style={{ padding: "16px 20px", borderBottom: "1px solid #e5e7eb" }}>
        <h2
          style={{
            margin: 0,
            fontSize: 18,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          마스터 세번관리
          <span
            style={{
              fontSize: 10,
              fontWeight: 500,
              color: "#9ca3af",
              border: "1px solid #d1d5db",
              borderRadius: 4,
              padding: "0 5px",
              lineHeight: "16px",
            }}
          >
            beta
          </span>
        </h2>
      </div>
      <div
        style={{
          padding: "12px 20px",
          borderBottom: "1px solid #e5e7eb",
          display: "flex",
          gap: 8,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <select
          value={searchMode}
          onChange={(e) => {
            const mode = e.target.value;
            setSearchMode(mode);
            setBanner("");
            setDeleteArmed(false);
            if (mode === "similar") {
              setHskFilter(""); // 숨은 필터가 이후 조회에 재적용되는 것 방지
              setItems([]); // 유사 검색 전 빈 상태(이전 결과 잔존 방지)
            } else {
              reload(q, ""); // 일반 복귀 — rate 잔존 결과를 일반 목록으로 교체
            }
          }}
          style={{
            padding: "5px 8px",
            border: "1px solid #d1d5db",
            borderRadius: 6,
            fontSize: 13,
          }}
        >
          <option value="normal">일반</option>
          <option value="similar">유사</option>
        </select>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onSearch();
          }}
          placeholder="상품명/성분/공급자/세번 검색"
          style={{
            width: 240,
            padding: "5px 8px",
            border: "1px solid #d1d5db",
            borderRadius: 6,
          }}
        />
        <Btn onClick={onSearch}>검색</Btn>
        <span
          style={{
            display: "inline-flex",
            gap: 6,
            alignItems: "center",
            marginLeft: 12,
          }}
        >
          세번
          <input
            value={hskInput}
            onChange={(e) => setHskInput(e.target.value)}
            placeholder="10자리"
            style={{
              width: 120,
              padding: "5px 8px",
              border: "1px solid #d1d5db",
              borderRadius: 6,
              fontFamily: "monospace",
            }}
          />
          <Btn onClick={applyBulkHsk}>체크행에 입력</Btn>
        </span>
        <Btn onClick={onDeleteClick}>삭제</Btn>
        <Btn onClick={onDownload} disabled={!items.length}>
          엑셀 다운로드
        </Btn>
        {loading && (
          <span style={{ fontSize: 12, color: "#6b7280", marginLeft: 8 }}>
            불러오는 중…
          </span>
        )}
      </div>
      {banner && (
        <div
          style={{
            padding: "8px 20px",
            background: "#fef3c7",
            color: "#92400e",
            fontSize: 13,
            borderBottom: "1px solid #fde68a",
          }}
        >
          {banner}
        </div>
      )}
      <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
        <div
          style={{
            width: 240,
            borderRight: "1px solid #e5e7eb",
            overflowY: "auto",
            padding: "12px 8px",
            fontSize: 13,
            opacity: searchMode === "similar" ? 0.45 : 1,
            pointerEvents: searchMode === "similar" ? "none" : "auto",
          }}
        >
          {summaryError && (
            <div style={{ color: "#6b7280", padding: "4px 8px" }}>
              집계를 불러오지 못했습니다.{" "}
              <span
                onClick={reloadSummary}
                style={{ textDecoration: "underline", cursor: "pointer" }}
              >
                재시도
              </span>
            </div>
          )}
          {!summaryError && (
            <>
              <div
                onClick={() => onPickHsk("")}
                style={{
                  padding: "5px 8px",
                  cursor: "pointer",
                  borderRadius: 4,
                  background: hskFilter === "" ? "#dbeafe" : "transparent",
                  fontWeight: 600,
                }}
              >
                전체 ({summaryTotal.toLocaleString("ko-KR")})
              </div>
              {summary.map((s) => (
                <div
                  key={s.hsk}
                  onClick={() => onPickHsk(s.hsk)}
                  style={{
                    padding: "5px 8px",
                    cursor: "pointer",
                    borderRadius: 4,
                    background: hskFilter === s.hsk ? "#dbeafe" : "transparent",
                    fontFamily: "monospace",
                  }}
                >
                  {s.label}
                </div>
              ))}
            </>
          )}
        </div>
        <div style={{ flex: 1, padding: 20, overflow: "hidden" }}>
          <div id={gridId} style={{ width: "100%", height: "100%" }} />
        </div>
      </div>
      <style>{`
        .audit-grid-mono { font-family: 'JetBrains Mono', 'Roboto Mono', monospace; font-size: 12px; }
      `}</style>
    </div>
  );
}
